import { GridSortModel } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { getRealm } from "../../../AuthProvider";
import {
  PatientIntakeForm,
  PatientIntakeFormControllerApi,
} from "../../../services";
import { Identified } from "../../../types/sdk-types";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
} from "../../../utils/utils";

interface Props {
  patientUuid?: string;
  appointmentUuid?: string;
  pagination: DGT_Pagination;
  gridSorting?: GridSortModel;
  sortBy?: string;
  sortDirection?: string;
  searchString?: string;
  xTENANTID?: string;
  isPublic?: boolean;
  enabled?: boolean;
}

/**
 *
 * @author Rajkumar Rathod
 */
export const usePatientIntakeFormList = ({
  patientUuid,
  appointmentUuid,
  pagination,
  gridSorting,
  sortDirection,
  searchString,
  isPublic,
  xTENANTID,
  enabled,
}: Props): useBaseContent<Identified<PatientIntakeForm>> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);

  const sortDirect = gridSorting?.[0]?.sort ?? undefined;
  const sortBy = sortDirect ? "firstName" : undefined;

  // The open form reached from the emailed link resolves its realm from the
  // host, which is empty on localhost unless the link carries ?realm=. The API
  // layer only sets X-TENANT-ID when the value is non-null, so without this
  // fallback the header is dropped and the backend answers from its default
  // tenant.
  const tenantId =
    xTENANTID ??
    getXtenantId() ??
    getRealm(location.hostname?.split(".")) ??
    undefined;

  return useQuery({
    queryKey: [
      "patient-intake-form-list",
      pageIndex,
      pageSize,
      sortBy,
      patientUuid,
      appointmentUuid,
      sortDirection,
      tenantId,
    ],
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: (!!patientUuid || !!appointmentUuid) && enabled,
    queryFn: async () =>
      await new PatientIntakeFormControllerApi(
        isPublic ? undefined : getConfigObject()
      )
        .getPatientAllIntakeForm(
          pageIndex, //page
          pageSize, //size
          sortBy,
          patientUuid,
          appointmentUuid,
          sortDirection, // sortDirection
          tenantId // xTENANTID
        )
        .then((res) =>
          asPaginatedContent<Identified<PatientIntakeForm>>(res?.data)
        ),
  });
};
